import React, {ChangeEvent, useCallback, useRef, useState} from "react";
import {errorFlag, successFlag} from "../../model/flags";
import {useFlags} from "@atlaskit/flag";
import Button from "@atlaskit/button/new";
import UploadIcon from "@atlaskit/icon/glyph/upload";
import AddIcon from "@atlaskit/icon/glyph/add";
import {Menu} from "navigation-common/types/navigation-types";
import {ImportConfirmModal} from "./ImportConfirmModal";
import {ModalTransition} from "@atlaskit/modal-dialog";
import {ButtonGroup} from '@atlaskit/button';

interface Props {
    onMenuImported: (menu: Menu) => Promise<void>,
    onClickCreate: () => void,
    showCreateDemoMenu: boolean,
    onClickCreateDemo: () => void,
}

export const OverviewPageActions = (props: Props) => {
    const fileInput = useRef<HTMLInputElement>(null);
    const {showFlag} = useFlags();
    const [menuToImport, setMenuToImport] = useState<Menu>();
    const [isImportModalOpen, setImportModalOpen] = useState(false);
    const openImportModal = useCallback(() => setImportModalOpen(true), []);
    const closeImportModal = useCallback(() => setImportModalOpen(false), []);

    const handleImport = async (target: EventTarget & HTMLInputElement) => {
        if (!target.files) {
            console.error("Could not target file");
            return;
        }

        const file = target.files[0];

        if (fileInput?.current) {
            fileInput.current.value = "";
        }

        let newMenu: Menu;

        try {
            const uploadDataString = await file.text();
            newMenu = JSON.parse(uploadDataString);
        } catch (e) {
            showFlag(errorFlag({title: "Import failed!", description: "Could not read menu file"}));
            return;
        }

        setMenuToImport(newMenu);
        openImportModal();
    };

    const onImport = async (newMenu: Menu) => {
        try {
            newMenu.anonymousAccess = false;
            newMenu.default = false;
            newMenu.userGroups = [];
            newMenu.spaceLabels = [];
            await props.onMenuImported(newMenu);
        } catch (e) {
            console.error("Could not import menu");
            return;
        } finally {
            closeImportModal();
            setMenuToImport(undefined);
        }

        showFlag(successFlag({title: "Success", description: "Menu has been imported."}));
    };

    const onCancelImport = () => {
        closeImportModal();
        setMenuToImport(undefined);
    };

    return (
        <>
            <ButtonGroup>
                <Button onClick={() => fileInput.current!.click()}
                        iconBefore={(iconProps) => (
                            <UploadIcon {...iconProps} size="small" label={"import menu"}/>
                        )}
                        appearance="default"
                        isLoading={false}>
                    Import menu
                </Button>
                <input type="file" id="importMenuInput" accept=".json" ref={fileInput}
                       onChange={(e: ChangeEvent<HTMLInputElement>) => handleImport(e.target)}
                       style={{display: "none"}}
                />
                <Button appearance={"primary"} iconBefore={(iconProps) => (
                    <AddIcon {...iconProps} size="small" label={"Create menu"}/>
                )} onClick={() => props.onClickCreate()}>
                    Create menu
                </Button>
                {props.showCreateDemoMenu &&
                    <div className={"demo-menu-button"}><Button onClick={() => props.onClickCreateDemo()}>Create Demo Menu</Button></div>
                }
            </ButtonGroup>
            <ModalTransition>
                {isImportModalOpen && menuToImport && (<ImportConfirmModal menuToImport={menuToImport} onImport={onImport} onCancel={onCancelImport}/>)}
            </ModalTransition>
        </>
    );
};
